---
title: Details
description: A detailed summary of all the features for ShipRapid
seo:
  title: Details
  description: A detailed summary of all the features for ShipRapid by L13o S.r.l
---

<script>
    import Mermaid from "$internal/components/mermaid/Mermaid.svelte";
</script>

## Why [SvelteKit](https://kit.svelte.dev/)

It's the framework you've been waiting for to create lightning-fast web applications with the simplicity and elegance of traditional web technologies.

While some other frameworks rely on a virtual DOM, SvelteKit **compiles your components** into highly efficient JavaScript **at build time**. This means smaller bundle sizes and lightning-fast load times, ensuring a snappy user experience.

You'll fall in love with the templating system that is so intuitive you'll think HTML, CSS, and JS should always have been so.

It seamlessly integrates **SSR** into your application, ensuring both speed and SEO-friendliness.

We've gone the extra mile by integrating SvelteKit with [TypeScript](https://www.typescriptlang.org/). This choice enhances not only the stability of your application but also its scalability. 

## Easy Authentication with Stytch

Our template is integrated with [Stytch](https://stytch.com/) for **authentication**. It is the ideal choice for a startup that values flexibility, security, and a hassle-free testing environment.

### Providers

We've made it even easier for you by including two highly convenient authentication methods right out of the box:
1. **Google OAuth**: Users can quickly log in or sign up using their Google accounts, enhancing the user experience and reducing friction during onboarding.
1. **MagicLink**: Say goodbye to password-related hassles. MagicLink is a secure and user-friendly authentication method. Users receive a link via email, click it, and they're in. It's a game-changer for quick and secure access.

You are not limited to just these; when the time is right you can easily **expand to other providers** like Facebook, Apple, or GitHub and [many more](https://stytch.com/docs/api/oauth-github-start). If ever your startup needs it (we hope so!) you can even expand to [OTP](https://stytch.com/docs/api/send-otp-by-sms), [WebAuthn](https://stytch.com/docs/api/webauthn-register-start), [M2M](https://stytch.com/docs/api/m2m-client)!

The testing process **does not require a credit card** during the test environment, allowing you to experiment and fine-tune your authentication methods without financial constraints.

### Server-Side Renderer Dashboard

Our template comes with a **Server-Side Rendering** (SSR) dashboard. This powerful feature enables you to serve dynamic content efficiently and boost your application's performance.

We've taken security seriously. The template includes a robust system for **protected routes and endpoints**. You can ensure that only authorized users access sensitive parts of your application.

## Efficient Payment Processing with Stripe

Our template lays a solid foundation for processing transactions efficiently. What sets it apart is the pre-integrated [Stripe](https://stripe.com/) webhook, which offers a valuable head start in payment management. You don't have to begin from scratch; instead, you can build upon this foundation to expand and enhance your payment system.

As with Stytch, the testing environment for the Stripe webhook also doesn't require any credit card, providing a hassle-free and cost-effective space for you to experiment and fine-tune your payment processes.

## Database Foundation

Our database of choice is [MongoDB](https://www.mongodb.com/), a powerful and flexible NoSQL database. For enhanced convenience and structure, we've paired MongoDB with [Mongoose](https://mongoosejs.com/), a robust ODM (*Object Data Modeling*) library for Node.js.

It's thoughtfully integrated with other essential components, [authentication](#easy-authentication-with-stytch) and [payments](#efficient-payment-processing-with-stripe).

We've streamlined your local development setup. With just a simple `docker-compose` command, you can have your MongoDB environment up and running, ready for testing and development. We've included an admin panel to make your local development experience even more convenient.

## Powerful Static Site Generation (SSG)

<Mermaid id="how-content-works" code={`flowchart TB
Contents([/src/contents/**/*.md]) --> SvelteKit(SvelteKit)
SvelteKit --> Mdsvex{{mdsvex}}
Mdsvex --> SvelteKit
Mdsvex --> HTML[[.html]]`} class="float-right w-40 ml-2" />

Our template takes advantage of the robust SvelteKit functionalities to offer an efficient and flexible Static Site Generation (SSG) mechanism.


Creating and managing content is a breeze. You only need to add a markdown file to the `content` folder, and our system transforms it into SEO-optimized HTML. The best part is, the nesting in the filesystem is mirrored in the URL, ensuring a clear and structured website hierarchy.

You're not limited to static pages. Thanks to [mdsvex](https://mdsvex.com/), you can inject Svelte components and logic directly into your markdown files. This opens up a world of possibilities, allowing you to create **dynamic documentation**, **engaging blogs**, or any other content you can imagine.

## SEO Batteries Included

When it comes to SEO, we've got you covered. Our template includes a comprehensive set of features to boost your website's search engine visibility:

- **Meta Tags**: We've taken care of all the main meta tags, including **Open Graph** (*OG*) metatags and **Twitter** metatags. These ensure that your content is displayed optimally when shared on social media platforms.
- `robots.txt`: This file guides web crawlers on how to interact with your site, enhancing your site's visibility and control over search engine indexing.
- **Sitemap Dynamic Generation**: We've automated the generation of a `sitemap.xml` file. This is an essential tool for search engines to understand the structure of your website, making it easier for your content to be indexed.

## Stylish Design with TailwindCSS

Our template is designed with style in mind, thanks to [TailwindCSS](https://tailwindcss.com/). But it doesn't stop there – we've enhanced it further with [DaisyUI](https://daisyui.com/), guaranteeing speed, consistency, and a wide range of ready-to-use components.

- **20 Ready-Made Themes**: Choose from a selection of 20 distinct themes to effortlessly match your website's style to your brand. Customizing your theme is easy too.
- **Light and Dark Mode**: Stay in tune with modern design trends by offering both light and dark mode options to cater to your users' preferences.
- **Optimized Components**: Our components are meticulously designed not just for aesthetics but also for performance. Whenever possible, we offload computation to CSS, ensuring a snappy user experience. They are also optimized for responsiveness across all screen sizes, guaranteeing a consistent and engaging design.